import * as React from 'react';
import styled from 'styled-components';
import useVisibilitySensor from '@rooks/use-visibility-sensor';

import { animated, useSpring } from 'react-spring';
import { useImageLoaded } from '../../utils/hooks/useImageLoaded';
import { Maybe } from '../../../client/types/generated/types';

import { CardFallback } from '../layout/CardFallback';
import { ImageType } from '../../../server/global/enum';
import { isExternalURL } from '../../utils/helpers/url';

const ImageStyle = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  max-height: 650px;
  overflow: hidden;
  background: rgb(20, 20, 20);

  @media all and (max-width: 600px) {
    height: 300px;
  }

  img {
    width: 100%;
    height: 600px;
    object-fit: cover;
    object-position: center;

    @media all and (max-width: 600px) {
      height: 300px;
    }
  }

  .pm-logo {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    height: 20%;
    path {
      fill: rgb(232, 200, 147);
    }
  }
`;

const LogoStyle = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  max-height: 650px;
  overflow: hidden;
  background: rgb(20, 20, 20);

  @media all and (max-width: 600px) {
    height: 300px;
  }

  img {
    width: 100%;
    height: 50%;
    object-fit: contain;
    margin: auto;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    @media all and (max-width: 600px) {
      height: 38%;
      margin: 4em 0 auto 0;
    }
  }

  .pm-logo {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    height: 20%;
    path {
      fill: rgb(232, 200, 147);
    }
  }
`;

export const CoverImage: React.FC<{
  url: Maybe<string> | undefined;
  type?: Maybe<string> | undefined;
}> = ({ url, type }) => {
  const [imageUrl] = React.useState(isExternalURL(url).link);
  const imageRef = React.useRef<any>();
  const [imgType, setImgType] = React.useState(type);

  const { isVisible } = useVisibilitySensor(imageRef, {
    intervalCheck: false,
    scrollCheck: true,
    scrollDebounce: 30,
    resizeCheck: true,
    shouldCheckOnMount: true,
    minTopValue: -100,
    partialVisibility: true,
  });

  const { loaded, error } = useImageLoaded({
    url: imageUrl,
    visible: isVisible,
  });

  const fadeIn: any = useSpring({
    opacity: loaded ? 1 : 0,
    config: {
      duration: 200,
    },
  });

  if (url && url.length > 0) {
    if (imgType == ImageType.Logo) {
      return (
        <LogoStyle ref={imageRef}>
          <animated.img
            style={fadeIn}
            data-src={imageUrl}
            src={imageUrl as string}
            alt=""
          />
        </LogoStyle>
      );
    }

    return (
      <ImageStyle ref={imageRef}>
        <animated.img
          style={fadeIn}
          data-src={imageUrl}
          src={imageUrl as string}
          alt=""
        />
      </ImageStyle>
    );
  }

  return (
    <ImageStyle ref={imageRef}>
      <CardFallback className="pm-logo" />
    </ImageStyle>
  );
};
